import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Grid, Tooltip } from '@mui/material';
import { ArrowBackIos, Save } from '@mui/icons-material';
import { Dropdown } from 'primereact/dropdown';
import WarningIcon from '@mui/icons-material/Warning';
import { toast } from 'react-toastify';
import { MultiSelect } from 'primereact/multiselect';
import './addEditSportPreferences.css';
import { InputNumber } from 'primereact/inputnumber';
import { ButtonIconStyled } from '../../../../views/devices/devicesStyles';
import { Header } from '../../../../components/header/header';
import {
  StyledHeaderContainer,
  StyledTitleHeader
} from '../../../../components/layout/main/mainStyles';
import Strings from '../../../../i18n';
import {
  useGetSportLeaguesMutation,
  useGetSportTeamsMutation,
  useGetSportTemplateByLeagueAndLocationMutation,
  useGetSportUserPreferencesMutation,
  useSetSportUserPreferencesMutation
} from '../../../../services/sportService/sportService';
import { PreferencePayload } from '../../../../types/sports/sportsInterfaces';
import { useGetSceneListByLocationMutation } from '../../../../services/scenes/sceneService';
import { useGetSequenceListByLocationMutation } from '../../../../services/sequences/sequencesService';
import { useGetEffectsByLocationMutation } from '../../../../services/effects/effectsService';
import { useGetTrimLightsByLocationMutation } from '../../../../services/trimLights/trimLightsService';
import { ISequence } from '../../../../types/sequences/sequencesInterface';

export const AddEditSportPreference = () => {
  const navigate = useNavigate();
  const { locationId, sportId } = useParams();
  const [selectedLeague, setSelectedLeague] = useState<any>(null);
  const [selectedTeam, setSelectedTeam] = useState<any>(null);

  const [getSportUserPreferences, { data: sportUserPreferences }] =
    useGetSportUserPreferencesMutation();

  const [getSportLeagues, { data: leagues, isLoading: isLoadingLeagues }] =
    useGetSportLeaguesMutation();

  const [getSportTeams, { data: teams, isLoading: isLoadingTeams, isSuccess }] =
    useGetSportTeamsMutation();

  const [getScenes, { data: scenes, isLoading: isLoadingScenes }] =
    useGetSceneListByLocationMutation();

  const [getSequences, { data: sequences, isLoading: isLoadingSequences }] =
    useGetSequenceListByLocationMutation();

  const [getEffectsByLocation, { data: effects, isLoading: isLoadingEffects }] =
    useGetEffectsByLocationMutation();

  const [
    getTrimLightsByLocation,
    { data: trimLights, isLoading: isLoadingTrimLights }
  ] = useGetTrimLightsByLocationMutation();

  const [
    postSportPreferences,
    {
      isLoading: isLoadingPostSportPreferences,
      isSuccess: successPostSportPreferences,
      isError: isErrorPostSportPreferences,
      error: errorPostSportPreferences
    }
  ] = useSetSportUserPreferencesMutation();

  const [
    getSportTemplateByLeagueAndLocation,
    { data: template, isLoading: isLoadingTemplate }
  ] = useGetSportTemplateByLeagueAndLocationMutation();

  const [preferencePayload, setPreferencePayload] = useState<PreferencePayload>(
    {
      details:
        template?.data?.map(() => ({
          id: 0,
          sportUserPreferenceTemplateId: null,
          entityId: null,
          thresholdMinutes: 0,
          duration: 0,
          channelConfigurations: []
        })) || []
    }
  );

  const requireScene = (entityType?: string) => entityType === 'SCENE';

  const handleDropdownChange = (index: number, value: number | null) => {
    setPreferencePayload((prevState) => {
      const updatedDetails = [...prevState.details];
      if (!updatedDetails[index]) return prevState;
      updatedDetails[index].entityId = value;
      return { ...prevState, details: updatedDetails };
    });
  };

  const handleNumberChange = (
    index: number,
    value: number | null,
    field: 'thresholdMinutes' | 'duration'
  ) => {
    setPreferencePayload((prevState) => {
      const updatedDetails = [...prevState.details];
      if (!updatedDetails[index]) return prevState;
      updatedDetails[index][field] = value || 0;
      return { ...prevState, details: updatedDetails };
    });
  };

  const handleChannelConfigurationsChange = (
    index: number,
    value: number[]
  ) => {
    setPreferencePayload((prevState) => {
      const updatedDetails = [...prevState.details];
      if (!updatedDetails[index]) return prevState;
      updatedDetails[index].channelConfigurations = value;
      return { ...prevState, details: updatedDetails };
    });
  };

  const loadRequiredData = () => {
    if (locationId) {
      getScenes(Number(locationId));
      getSequences(Number(locationId));
      getEffectsByLocation(Number(locationId));
      getTrimLightsByLocation(Number(locationId));
    }
  };

  useEffect(() => {
    getSportLeagues();
    loadRequiredData();
    if (locationId) {
      getSportUserPreferences(Number(locationId));
    }
  }, []);

  useEffect(() => {
    if (sportUserPreferences && sportId) {
      const sportUser = sportUserPreferences.data.find(
        (sport) => sport.id === parseInt(sportId, 10)
      );

      setSelectedLeague({ name: sportUser?.league });
    }
  }, [sportUserPreferences]);

  useEffect(() => {
    if (
      teams &&
      isSuccess &&
      sportUserPreferences &&
      !selectedTeam &&
      sportId
    ) {
      const sportUser = sportUserPreferences.data.find(
        (sport) => sport.id === parseInt(sportId, 10)
      );
      const teamSelected = teams.data.find(
        (team) => team.id === sportUser?.teamId
      );
      setSelectedTeam(teamSelected);
      if (sportUser)
        setPreferencePayload({
          details: sportUser?.details.map((detail, index) => {
            return {
              id: detail.id,
              entityId: detail.entityId,
              thresholdMinutes: detail.thresholdMinutes,
              duration: detail.duration,
              channelConfigurations: detail.channelConfigurations.map(
                (config) => config.id
              ),
              sportUserPreferenceTemplateId: index,
              durationRequired: detail.durationRequired,
              thresholdTimeRequired: detail.thresholdTimeRequired,
              key: detail.key,
              entityType: detail.entityType,
              preferenceName: detail.preferenceName
            };
          })
        });
    }
  }, [isSuccess]);

  useEffect(() => {
    if (selectedLeague) {
      getSportTeams(selectedLeague.name);
      setSelectedTeam(null);

      getSportTemplateByLeagueAndLocation({
        league: selectedLeague.name,
        locationId: Number(locationId)
      });
    }
  }, [selectedLeague]);

  useEffect(() => {
    if (template?.data && !sportId) {
      setPreferencePayload((prevState) => ({
        ...prevState,
        details: template.data.map((_, index) => ({
          id: prevState.details[index]?.id || 0,
          sportUserPreferenceTemplateId: template.data[index].id,
          entityId: prevState.details[index]?.entityId || null,
          thresholdMinutes: prevState.details[index]?.thresholdMinutes || 0,
          duration: prevState.details[index]?.duration || 0,
          channelConfigurations:
            prevState.details[index]?.channelConfigurations || []
        }))
      }));
    }
  }, [template]);

  const handleSave = () => {
    const payload = {
      id: sportId ? parseInt(sportId, 10) : 0,
      teamId: selectedTeam?.id,
      locationId: Number(locationId),
      executionOrder: 0,
      enabled: true,
      details: preferencePayload.details
    };
    postSportPreferences(payload);
  };

  useEffect(() => {
    if (successPostSportPreferences) {
      toast.success(Strings.sports.sportPreferenceSuccess);
      navigate(`/locations/${locationId}/applications/game_day`);
    }
  }, [successPostSportPreferences]);

  useEffect(() => {
    if (isErrorPostSportPreferences) {
      toast.error(
        (errorPostSportPreferences as { data: { message: string } }).data
          .message
      );
    }
  }, [isErrorPostSportPreferences]);

  const validateLastThreeItems = (data: any) => {
    const lastThree = data.slice(-3);

    return lastThree.every((item: any) => {
      if (item.entityId !== null) {
        return (
          Array.isArray(item.channelConfigurations) &&
          item.channelConfigurations.length > 0
        );
      }
      return true;
    });
  };

  return (
    <>
      <Header>
        <StyledHeaderContainer>
          <div className="row justify-content-between">
            <div className="col-4 text-left">
              <ButtonIconStyled
                size="small"
                onClick={() =>
                  navigate(`/locations/${locationId}/applications/game_day`)
                }
              >
                <ArrowBackIos />
              </ButtonIconStyled>
            </div>
            <div className="col-4 text-center">
              <StyledTitleHeader>
                {Strings.sports.newSportPreference}
              </StyledTitleHeader>
            </div>
            <div className="col-4 text-right">
              {!preferencePayload.details.some(
                (item) => item.entityId !== null
              ) && (
                <Tooltip title={Strings.common.noConfig}>
                  <ButtonIconStyled size="small" disabled={false}>
                    <WarningIcon color="warning" />
                  </ButtonIconStyled>
                </Tooltip>
              )}

              <Tooltip title="Save">
                <ButtonIconStyled size="small" onClick={handleSave}>
                  <Save />
                </ButtonIconStyled>
              </Tooltip>
            </div>
          </div>
        </StyledHeaderContainer>
      </Header>
      <div className="container">
        <div className="row">
          <div className="col-12 text-center my-2">
            <Dropdown
              value={selectedLeague}
              onChange={(e) => setSelectedLeague(e.value)}
              options={leagues?.data || []}
              optionLabel="name"
              placeholder="Select a League"
              className="w-full md:w-14rem mx-2"
              disabled={!!sportId}
            />

            <Dropdown
              value={selectedTeam}
              onChange={(e) => setSelectedTeam(e.value)}
              options={teams?.data || []}
              optionLabel="name"
              placeholder="Select a Team"
              className="w-full md:w-14rem mx-2"
              disabled={!!sportId}
            />
            {!validateLastThreeItems(preferencePayload.details) && (
              <Tooltip title={Strings.common.noTarget}>
                <ButtonIconStyled size="small" disabled={false}>
                  <WarningIcon color="warning" />
                </ButtonIconStyled>
              </Tooltip>
            )}
          </div>
          {!sportId &&
            template?.data &&
            selectedLeague &&
            selectedTeam &&
            preferencePayload.details.length === template.data.length && (
              <div className="col-12">
                <div className="row justify-content-center align-items-center mx-2">
                  {template.data.map((item, index) => (
                    <div
                      key={item.key}
                      className="col-12 col-md-6 col-lg-4 p-2"
                    >
                      <h4 className="title-preference">{item.name}</h4>
                      <div className="container-preference">
                        <p>Target:</p>
                        {item.entityType !== 'EFFECT' ? (
                          <Dropdown
                            name={item.key}
                            value={
                              preferencePayload.details[index]?.entityId || null
                            }
                            options={
                              (requireScene(item.entityType)
                                ? scenes
                                : (sequences as unknown as ISequence[])) ?? []
                            }
                            onChange={(e) =>
                              handleDropdownChange(index, e.value)
                            }
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select an option"
                            showClear
                          />
                        ) : (
                          <Dropdown
                            name={item.key}
                            value={
                              preferencePayload.details[index]?.entityId || null
                            }
                            options={effects}
                            onChange={(e) =>
                              handleDropdownChange(index, e.value)
                            }
                            optionLabel="name"
                            optionValue="id"
                            placeholder="Select an option"
                            showClear
                          />
                        )}

                        {(item.durationRequired ||
                          item.thresholdTimeRequired) &&
                          preferencePayload.details[index]?.entityId && (
                            <div>
                              <label
                                className="mt-2"
                                htmlFor={`${item.key}_time`}
                                style={{ display: 'block' }}
                              >
                                {requireScene(item.entityType)
                                  ? 'Threshold Minutes'
                                  : 'Duration (Seconds)'}
                                :
                              </label>
                              <InputNumber
                                value={
                                  requireScene(item.entityType)
                                    ? preferencePayload.details[index]
                                        ?.thresholdMinutes || 0
                                    : preferencePayload.details[index]
                                        ?.duration || 0
                                }
                                onValueChange={(e) =>
                                  handleNumberChange(
                                    index,
                                    e.value || 0,
                                    requireScene(item.entityType)
                                      ? 'thresholdMinutes'
                                      : 'duration'
                                  )
                                }
                                showButtons
                                buttonLayout="horizontal"
                                incrementButtonClassName="p-button-secondary"
                                decrementButtonClassName="p-button-secondary"
                                incrementButtonIcon="pi pi-plus"
                                decrementButtonIcon="pi pi-minus"
                                size={10}
                                min={0}
                                required
                              />
                            </div>
                          )}

                        {item.entityType === 'EFFECT' &&
                          preferencePayload.details[index]?.entityId && (
                            <div
                              className="card flex justify-content-center"
                              style={{
                                background: 'transparent',
                                border: 'none'
                              }}
                            >
                              <p className="pb-0 mb-0 mt-2">Trim Lights:</p>
                              <MultiSelect
                                display="chip"
                                className="w-full md:w-20rem"
                                name={item.key}
                                value={
                                  preferencePayload.details[index]
                                    ?.channelConfigurations || []
                                }
                                options={trimLights}
                                onChange={(e) => {
                                  handleChannelConfigurationsChange(
                                    index,
                                    e.value
                                  );
                                }}
                                optionLabel="name"
                                optionValue="id"
                                placeholder="Select an option"
                                showClear
                              />
                            </div>
                          )}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          {sportId && preferencePayload.details.length && (
            <div className="col-12">
              <div className="row justify-content-center align-items-center mx-2">
                {preferencePayload.details.map((item, index) => (
                  <div key={item.key} className="col-12 col-md-6 col-lg-4 p-2">
                    <h4 className="title-preference">{item.preferenceName}</h4>
                    <div className="container-preference">
                      <p>Target:</p>
                      {item.entityType !== 'EFFECT' ? (
                        <Dropdown
                          name={item.key}
                          value={
                            preferencePayload.details[index]?.entityId || null
                          }
                          options={
                            (requireScene(item.entityType)
                              ? scenes
                              : (sequences as unknown as ISequence[])) ?? []
                          }
                          onChange={(e) => handleDropdownChange(index, e.value)}
                          optionLabel="name"
                          optionValue="id"
                          placeholder="Select an option"
                          showClear
                        />
                      ) : (
                        <Dropdown
                          name={item.key}
                          value={
                            preferencePayload.details[index]?.entityId || null
                          }
                          options={effects}
                          onChange={(e) => handleDropdownChange(index, e.value)}
                          optionLabel="name"
                          optionValue="id"
                          placeholder="Select an option"
                          showClear
                        />
                      )}

                      {(item.durationRequired || item.thresholdTimeRequired) &&
                        preferencePayload.details[index]?.entityId && (
                          <div>
                            <label
                              className="mt-2"
                              htmlFor={`${item.key}_time`}
                              style={{ display: 'block' }}
                            >
                              {requireScene(item.entityType)
                                ? 'Threshold Minutes'
                                : 'Duration (Seconds)'}
                              :
                            </label>
                            <InputNumber
                              value={
                                requireScene(item.entityType)
                                  ? preferencePayload.details[index]
                                      ?.thresholdMinutes || 0
                                  : preferencePayload.details[index]
                                      ?.duration || 0
                              }
                              onValueChange={(e) =>
                                handleNumberChange(
                                  index,
                                  e.value || 0,
                                  requireScene(item.entityType)
                                    ? 'thresholdMinutes'
                                    : 'duration'
                                )
                              }
                              showButtons
                              buttonLayout="horizontal"
                              incrementButtonClassName="p-button-secondary"
                              decrementButtonClassName="p-button-secondary"
                              incrementButtonIcon="pi pi-plus"
                              decrementButtonIcon="pi pi-minus"
                              size={10}
                              min={0}
                              required
                            />
                          </div>
                        )}

                      {item.entityType === 'EFFECT' &&
                        preferencePayload.details[index]?.entityId && (
                          <div
                            className="card flex justify-content-center"
                            style={{
                              background: 'transparent',
                              border: 'none'
                            }}
                          >
                            <p className="pb-0 mb-0 mt-2">Trim Lights:</p>
                            <MultiSelect
                              display="chip"
                              className="w-full md:w-20rem"
                              name={item.key}
                              value={
                                preferencePayload.details[index]
                                  ?.channelConfigurations || []
                              }
                              options={trimLights}
                              onChange={(e) => {
                                handleChannelConfigurationsChange(
                                  index,
                                  e.value
                                );
                              }}
                              optionLabel="name"
                              optionValue="id"
                              placeholder="Select an option"
                              showClear
                            />
                          </div>
                        )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};
